import { color, scaleSequential, ScaleSequential, ScaleTime } from "d3"
import { getSpectrumInterpolator } from "../../../../Types/ColorSpectrum"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { CompositeTimeSeriesData, isCompositeTimeSeriesData } from "../../../../Data/TimeSeriesData"
import { getSpectrogramFrequencyLabels, PageRectangle, SpectrogramTraceConfig, SPECTROGRAM_EPOCH_MS } from "../../../../Types/Trace"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { D3Trace } from "./D3Trace"
import { ReactCallbacks } from "../../../../Types/ReactCallbacks"
import { TraceRenderStrategy, TraceRendererOptions } from "./RenderStrategy"

export class SpectrogramRenderStrategy extends TraceRenderStrategy {
	private colorScale: ScaleSequential<string>
	private rendererStub = {
		context: (_ctx?: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null) => undefined,
	}

	private colorLUT = new Uint8Array(256 * 3)

	private pageManager: TimeSeriesPageManager<ModalityPage>
	private config: SpectrogramTraceConfig
	private d3Trace: D3Trace

	constructor(pageManager: TimeSeriesPageManager<ModalityPage>, d3Trace: D3Trace, config: SpectrogramTraceConfig) {
		super()
		this.pageManager = pageManager
		this.config = config
		this.d3Trace = d3Trace

		this.colorScale = scaleSequential(getSpectrumInterpolator(this.config.colorSpectrum)).domain([this.config.lowerBound, this.config.upperBound])
		this.buildColorLUT()
	}

	private buildColorLUT(): void {
		const { lowerBound, upperBound } = this.config
		const range = upperBound - lowerBound

		for (let i = 0; i < 256; i++) {
			const normalizedValue = lowerBound + (i / 255) * range
			const hexColor = this.colorScale(normalizedValue)
			const rgb = this.parseColor(hexColor)
			this.colorLUT[i * 3] = rgb.r
			this.colorLUT[i * 3 + 1] = rgb.g
			this.colorLUT[i * 3 + 2] = rgb.b
		}
	}

	private parseColor(colorString: string): { r: number; g: number; b: number } {
		const parsed = color(colorString)
		if (parsed) {
			const rgb = parsed.rgb()
			return { r: rgb.r, g: rgb.g, b: rgb.b }
		}
		return { r: 255, g: 255, b: 255 }
	}

	private applyDisplayScale(power: number): number {
		// Query serves canonical DSA power (uV^2); display scale is applied here,
		// client-side, so changing it is an instant re-render with no refetch.
		switch (this.config.displayScale) {
			case "voltage":
				return Math.sqrt(Math.max(power, 0))
			case "sqrt_voltage":
				return Math.sqrt(Math.sqrt(Math.max(power, 0)))
			case "power":
			default:
				return power
		}
	}

	private getColorFromLUT(rawValue: number | undefined): [number, number, number] | null {
		if (rawValue === undefined || Number.isNaN(rawValue)) {
			return null
		}

		const value = this.applyDisplayScale(rawValue)
		const { lowerBound, upperBound } = this.config
		const normalized = (value - lowerBound) / (upperBound - lowerBound)

		// Clamp out-of-range values to the color-map endpoints. The LUT is already
		// built across the display-scaled bounds, so this matches the sequential
		// scale's clamped output while keeping the d3 color parse out of the
		// per-cell hot loop (it dominated on noisy EEG, where power routinely
		// exceeds the upper bound).
		const lutStep = normalized <= 0 ? 0 : normalized >= 1 ? 255 : Math.round(normalized * 255)
		const lutIndex = lutStep * 3
		return [this.colorLUT[lutIndex], this.colorLUT[lutIndex + 1], this.colorLUT[lutIndex + 2]]
	}

	public renderPage(
		page: ModalityPage,
		reactCallbacks: ReactCallbacks<any>,
		offscreenXScale: ScaleTime<any, any, any>,
		offscreenCanvas: OffscreenCanvas,
		context: CanvasRenderingContext2D,
		offscreenContext: OffscreenCanvasRenderingContext2D,
		pageRectangle: PageRectangle,
		unloadedRectangle: PageRectangle | null,
	): void {
		const { x, y, width, height } = pageRectangle
		const dataObjectId = reactCallbacks.dataSourceMap.get(this.config.dataSource) ?? Infinity
		const traceData = page.data.get(dataObjectId)?.get(this.config.dataKey)
		const compositeData = traceData !== undefined && isCompositeTimeSeriesData(traceData) ? traceData : undefined

		const drawWidth = Math.max(1, Math.round(width))
		const drawHeight = Math.max(1, Math.round(height))

		// A persisted config with reversed or equal bounds would normalize every cell
		// to NaN, so render the placeholder instead of a black spectrogram.
		if (!(this.config.upperBound > this.config.lowerBound)) {
			offscreenContext.fillStyle = this.getUnloadedColor()
			offscreenContext.fillRect(0, 0, drawWidth, drawHeight)
		} else if (compositeData && compositeData.data.length > 0 && compositeData.times.length > 0) {
			offscreenXScale.domain([page.startTime, page.endTime]).range([0, drawWidth])
			const samplingPeriod = page.timingInformation?.get(dataObjectId)?.get(this.config.dataKey)?.samplingPeriod

			this.drawSpectrogram(offscreenContext, compositeData, drawWidth, drawHeight, offscreenXScale, samplingPeriod)
		}

		// After the data draw: drawSpectrogram writes the page area with
		// putImageData, which replaces pixels instead of compositing, and would
		// wipe a fill made before it.
		this.offscreenFillInUnloadedRegion(offscreenContext, unloadedRectangle)

		const bitmap = offscreenCanvas.transferToImageBitmap()
		page.updateRenderCache(dataObjectId, this.getRenderCacheKey(), { bitmap, dirty: false, edges: [] })
		context.drawImage(bitmap, Math.round(x), Math.round(y))
	}

	getRenderCacheKey(): string {
		const { graphId, dataKey, dataSource, colorSpectrum, lowerBound, upperBound, displayScale, id } = this.config
		const frequencyLabels = getSpectrogramFrequencyLabels(this.config)
		const frequencyCrop = `${frequencyLabels.length}:${frequencyLabels[0]}-${frequencyLabels[frequencyLabels.length - 1]}`
		// Missing/NaN cells are baked into the cached bitmaps with the themed
		// placeholder color, so the cache must be partitioned by color scheme.
		const colorScheme = document.documentElement.getAttribute("data-color-scheme") ?? "light"
		return [graphId, id, dataKey, dataSource, colorSpectrum, lowerBound, upperBound, displayScale, frequencyCrop, colorScheme].join("-")
	}

	public updateConfig(traceConfig: SpectrogramTraceConfig): void {
		this.config = traceConfig
		this.colorScale = scaleSequential(getSpectrumInterpolator(this.config.colorSpectrum)).domain([this.config.lowerBound, this.config.upperBound])
		this.buildColorLUT()
	}

	public getOffscreenRenderer = (_options?: TraceRendererOptions) => this.rendererStub

	public getDirectRenderer = (_options?: TraceRendererOptions) => this.rendererStub

	public render(): void {
		this.pageManager.getPagesInView().forEach(page => this.d3Trace.renderPage(page))
	}

	// A page's sampling period is an epoch, or a pixel-sized bin when query downsampled
	// the page. A period derived from the spacing of isolated epochs is a gap, not an
	// epoch, so a cell is never wider than the larger of those two.
	private getCellPeriod(xScale: ScaleTime<any, any, any>, width: number, samplingPeriod?: number | null): number {
		const [pageStart, pageEnd] = xScale.domain() as [Date, Date]
		const timeBetweenPixels = (pageEnd.getTime() - pageStart.getTime()) / Math.max(width, 1)
		const maxCellPeriod = Math.max(SPECTROGRAM_EPOCH_MS, 2 * timeBetweenPixels)
		const knownPeriod = samplingPeriod && Number.isFinite(samplingPeriod) ? samplingPeriod : SPECTROGRAM_EPOCH_MS
		return Math.min(Math.max(knownPeriod, SPECTROGRAM_EPOCH_MS), maxCellPeriod)
	}

	private drawSpectrogram(
		context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
		compositeData: CompositeTimeSeriesData,
		width: number,
		height: number,
		xScale: ScaleTime<any, any, any>,
		samplingPeriod?: number | null
	) {
		const timePoints = compositeData.times.length
		const frequencyLabels = getSpectrogramFrequencyLabels(this.config)

		if (timePoints === 0 || frequencyLabels.length === 0) {
			return
		}

		const yScale = this.config.yScale
		const yBandHeight = Math.max(yScale.bandwidth(), 1)
		const cellHeight = Math.ceil(yBandHeight)

		const startTime = xScale.domain()[0]
		const cellPeriod = this.getCellPeriod(xScale, width, samplingPeriod)
		const barWidth = Math.abs(xScale(new Date(startTime.getTime() + cellPeriod)) - xScale(startTime))
		const cellWidth = Math.max(Math.ceil(barWidth), 1)

		const seriesCount = Math.min(frequencyLabels.length, compositeData.data.length)

		// Get the y-offset to convert from graph coordinates to canvas-local coordinates
		const yRangeStart = yScale.range()[0]

		const yPositions: (number | undefined)[] = frequencyLabels.map(label => {
			const y = yScale(label)
			return y !== undefined ? Math.floor(y - yRangeStart) : undefined
		})

		const imageData = context.createImageData(width, height)
		const pixels = imageData.data

		const missingCellColor = this.parseColor(this.getUnloadedColor())

		for (let timeIndex = 0; timeIndex < timePoints; timeIndex++) {
			const time = compositeData.times[timeIndex]
			const xPosition = xScale(time)

			if (xPosition === undefined) {
				continue
			}

			const xStart = Math.floor(xPosition - cellWidth / 2)

			if (xStart + cellWidth <= 0 || xStart >= width) {
				continue
			}

			let xMin = Math.max(0, xStart)
			let xMax = Math.min(width, xStart + cellWidth)

			if (timeIndex === 0) {
				const pageStartTime = (xScale.domain()[0] as Date).getTime()
				if (time - pageStartTime < cellPeriod) {
					xMin = 0
				}
			}
			if (timeIndex === timePoints - 1) {
				const pageEndTime = (xScale.domain()[1] as Date).getTime()
				if (pageEndTime - time < cellPeriod) {
					xMax = width
				}
			}

			for (let freqIndex = 0; freqIndex < seriesCount; freqIndex++) {
				const row = compositeData.data[freqIndex]
				if (!row) continue

				const yStart = yPositions[freqIndex]
				if (yStart === undefined) continue

				if (yStart + cellHeight <= 0 || yStart >= height) {
					continue
				}

				const value = row[timeIndex]
				const rgb = this.getColorFromLUT(value)

				const r = rgb ? rgb[0] : missingCellColor.r
				const g = rgb ? rgb[1] : missingCellColor.g
				const b = rgb ? rgb[2] : missingCellColor.b

				const yMin = Math.max(0, yStart)
				const yMax = Math.min(height, yStart + cellHeight)

				for (let py = yMin; py < yMax; py++) {
					const rowOffset = py * width * 4
					for (let px = xMin; px < xMax; px++) {
						const idx = rowOffset + px * 4
						pixels[idx] = r
						pixels[idx + 1] = g
						pixels[idx + 2] = b
						pixels[idx + 3] = 255
					}
				}
			}
		}

		context.putImageData(imageData, 0, 0)
	}
}
